'use client';

import dynamic from 'next/dynamic';
import type { MapContainerProps } from '@/app/_components/shared/location-map/location-map.types';
import { ASPECT_RATIO_CLASS, MapErrorBoundary } from '@/app/_components/shared/location-map/utils';
import { useConsentManager } from '@/app/_hooks/use-consent-manager';
import { cn } from '@/app/_lib/utils';
import { ConsentPlaceholder } from './consent-placeholder';

// Dynamic import - MapContainer (and Google Maps) only loads after consent
const MapContainer = dynamic(
  () => import('./map-container').then((mod) => mod.MapContainer),
  {
    ssr: false,
    loading: () => (
      <div className="flex aspect-10/16 items-center justify-center bg-muted md:aspect-16/10">
        <div className="size-8 animate-spin rounded-full border-4 border-muted-foreground/20 border-t-muted-foreground" />
      </div>
    ),
  }
);

export function LocationMapClient({ locations, labels, moduleId, googleMapsConfig, trackingPrefix }: MapContainerProps) {
  const { hasConsent, isReady, openConsentDialog } = useConsentManager({
    category: 'statistics',
  });

  // Show loading spinner while checking consent status
  if (!isReady) {
    return (
      <div className="flex aspect-10/16 items-center justify-center bg-muted md:aspect-16/10">
        <div className="size-8 animate-spin rounded-full border-4 border-muted-foreground/20 border-t-muted-foreground" />
      </div>
    );
  }

  if (!hasConsent) {
    return <ConsentPlaceholder labels={labels} onOpenConsent={openConsentDialog} trackingPrefix={trackingPrefix} />;
  }

  return (
    <MapErrorBoundary
      fallback={
        <div className={cn('flex items-center justify-center bg-muted', ASPECT_RATIO_CLASS)}>
          <p className="text-muted-foreground">{labels?.error}</p>
        </div>
      }
    >
      <MapContainer locations={locations} labels={labels} moduleId={moduleId} googleMapsConfig={googleMapsConfig} trackingPrefix={trackingPrefix} />
    </MapErrorBoundary>
  );
}
